// src/redux/checkout.js
// ─────────────────────────────────────────────────────────────────────────────
// SHARED order-placement helpers for the web checkout screens — the ONE
// implementation of the "order-first, then pay" contract (Playbook 12
// §B3/§C4). Before this, each of the five place-order screens re-implemented
// the same "await createOrder → check requestStatus → read the new order's id →
// classify a 422" dance inline. This centralises it so every screen behaves
// identically and a new screen gets it for free.
// ─────────────────────────────────────────────────────────────────────────────

// 422 codes the customer can recover from by changing their delivery address.
export const OUT_OF_RANGE_CODES = ["OUT_OF_DELIVERY_RANGE", "VENDOR_COORDS_MISSING"];

/**
 * Pull the created order's id out of a createOrder thunk `action`, tolerating
 * the shapes the backend has returned: { order: { _id } } | { _id } | { orderId }.
 * @returns {string|null}
 */
export function extractCreatedOrderId(action) {
  const p = action?.payload;
  return p?.order?._id || p?._id || p?.orderId || null;
}

/**
 * Create the order and return a NORMALISED outcome — the shared decision point
 * for every web pay handler. It does not show toasts, clear carts, or redirect;
 * the caller does that per screen (keeping this pure/testable).
 *
 * The web createOrder thunks use apisauce-style thunks that `rejectWithValue`
 * on !ok, so a failed create surfaces as `action.error` or
 * `requestStatus !== "fulfilled"` with the backend body in `action.payload`.
 *
 * @param {object}   params
 * @param {Function} params.dispatch      redux dispatch
 * @param {Function} params.createOrder   the createAsyncThunk for this surface
 * @param {object}   params.payload       order-create body
 * @returns {Promise<{ ok, orderId, outOfRange, code, message, action }>}
 */
export async function createOrderFirst({ dispatch, createOrder, payload }) {
  const action = await dispatch(createOrder(payload));
  const orderId = extractCreatedOrderId(action);
  const fulfilled = !action?.error && action?.meta?.requestStatus === "fulfilled";

  if (fulfilled) {
    return { ok: true, orderId, outOfRange: false, code: null, message: null, action };
  }
  const code = action?.payload?.code || null;
  return {
    ok: false,
    orderId: null,
    outOfRange: OUT_OF_RANGE_CODES.includes(code),
    code,
    message: action?.payload?.message || null,
    action,
  };
}

const checkout = { OUT_OF_RANGE_CODES, extractCreatedOrderId, createOrderFirst };
export default checkout;
